import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/api";
import Button from "../components/atoms/Button";
import Input from "../components/atoms/Input";

/**
 * 로그인 2단계 — 이메일로 받은 인증코드 입력 → /admin/verify → 토큰 발급.
 */
function VerifyPage() {
  const navigate = useNavigate();
  const email = localStorage.getItem("email") || "";

  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [resending, setResending] = useState(false);
  const [resentNotice, setResentNotice] = useState("");

  // email 이 없으면 1단계로 되돌림
  useEffect(() => {
    if (!email) navigate("/login", { replace: true });
  }, [email, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/verify", { email, code });
      if (!data?.success) {
        setError(data?.alert || "인증에 실패했습니다.");
        return;
      }
      const token =
        data?.data?.access_token ||
        data?.data?.token?.access_token ||
        data?.data?.token;
      const refresh =
        data?.data?.refresh_token || data?.data?.token?.refresh_token;
      if (!token) {
        setError("토큰을 받지 못했습니다.");
        return;
      }
      localStorage.setItem("token", token);
      if (refresh) localStorage.setItem("refreshToken", refresh);
      // 인증 흐름에 사용한 email 키는 정리
      localStorage.removeItem("email");

      // 본인 프로필 즉시 캐시 (실패해도 진입 후 다시 시도됨)
      try {
        const me = await api.get("/manager/change-request/me");
        if (me?.data?.success) {
          localStorage.setItem("user", JSON.stringify(me.data.data));
        }
      } catch (_) {}

      navigate("/", { replace: true });
    } catch (err) {
      setError("서버에 연결할 수 없습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    if (resending) return;
    setResending(true);
    setResentNotice("");
    setError("");
    try {
      const { data } = await api.post("/admin/certify", { email });
      if (data?.success) {
        setResentNotice("인증번호를 다시 발송했습니다.");
      } else {
        setError(data?.alert || "재발송에 실패했습니다.");
      }
    } catch (err) {
      setError("서버에 연결할 수 없습니다.");
    } finally {
      setResending(false);
    }
  };

  return (
    <Wrapper>
      <Card onSubmit={submit}>
        <Brand>더트루 변경 요청</Brand>
        <Sub>이메일 인증</Sub>
        <Desc>
          {email}로 인증번호를 보냈습니다.
          <br />
          메일을 확인하고 6자리 숫자를 입력해주세요.
        </Desc>
        <Field>
          <Label>인증번호</Label>
          <Input
            inputMode="numeric"
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
            maxLength={6}
            autoFocus
            required
          />
        </Field>
        {error && <ErrorText>{error}</ErrorText>}
        {resentNotice && <Notice>{resentNotice}</Notice>}
        <Button type="submit" block disabled={submitting || code.length !== 6}>
          {submitting ? "확인 중..." : "로그인"}
        </Button>
        <Helpers>
          <Linklike type="button" onClick={resend} disabled={resending}>
            {resending ? "재발송 중..." : "인증번호 다시 받기"}
          </Linklike>
          <Linklike
            type="button"
            onClick={() => {
              localStorage.removeItem("email");
              navigate("/login", { replace: true });
            }}
          >
            이메일 다시 입력
          </Linklike>
        </Helpers>
      </Card>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #f6f7f9;
`;
const Card = styled.form`
  width: 100%;
  max-width: 420px;
  padding: 28px 24px;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: 14px;
`;
const Brand = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: #003580;
  text-align: center;
`;
const Sub = styled.div`
  font-size: 13px;
  color: #888;
  text-align: center;
`;
const Desc = styled.p`
  margin: 4px 0 8px;
  font-size: 13px;
  line-height: 1.6;
  color: #555;
  text-align: center;
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const Label = styled.label`
  font-size: 13px;
  color: #555;
`;
const ErrorText = styled.div`
  color: #fb4d44;
  font-size: 13px;
`;
const Notice = styled.div`
  color: #003580;
  font-size: 13px;
`;
const Helpers = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
`;
const Linklike = styled.button`
  border: 0;
  background: transparent;
  color: #003580;
  font-size: 13px;
  padding: 6px 0;
  &:disabled {
    color: #bbb;
  }
`;

export default VerifyPage;
